import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from 'components/common/types/Store.types';
import {
  getHasDirectSalesAllocationInvalidatingCampaignChange,
  getHasDirectSalesCampaignPlanLinkInvalidatingChange,
} from 'store/dealManagement/selectors/getHasDirectSalesCampaignLevelChanged';
import { changeDirectSalesData, clearCampaignPlanShareUrl } from 'store/dealManagement/reducer';
import useCampaignPostSaveRefresh from '../useCampaignPostSaveRefresh';

interface UseHandleCampaignFieldChanges {
  captureChangesBeforeSave: (forceCampaignLevelChange?: boolean) => void;
  handleAfterSave: () => Promise<void>;
}

export const useHandleCampaignFieldChanges = (): UseHandleCampaignFieldChanges => {
  const dispatch = useDispatch();
  const hadCampaignFieldChangesBeforeSave = useRef(false);
  const hadLinkInvalidatingChangesBeforeSave = useRef(false);
  const refreshAfterSave = useCampaignPostSaveRefresh();

  const hasAllocationInvalidatingCampaignChange = useSelector<Store, boolean>(
    getHasDirectSalesAllocationInvalidatingCampaignChange,
  );
  const hasCampaignPlanLinkInvalidatingChange = useSelector<Store, boolean>(
    getHasDirectSalesCampaignPlanLinkInvalidatingChange,
  );

  const captureChangesBeforeSave = useCallback(
    (forceCampaignLevelChange?: boolean): void => {
      hadCampaignFieldChangesBeforeSave.current = forceCampaignLevelChange ?? hasAllocationInvalidatingCampaignChange;
      hadLinkInvalidatingChangesBeforeSave.current = forceCampaignLevelChange ?? hasCampaignPlanLinkInvalidatingChange;
    },
    [hasAllocationInvalidatingCampaignChange, hasCampaignPlanLinkInvalidatingChange],
  );

  const handleAfterSave = useCallback(async (): Promise<void> => {
    if (hadLinkInvalidatingChangesBeforeSave.current) {
      dispatch(clearCampaignPlanShareUrl());
      dispatch(changeDirectSalesData({ isGenerateCampaignPlanEnabled: true }));
    }

    hadLinkInvalidatingChangesBeforeSave.current = false;

    if (!hadCampaignFieldChangesBeforeSave.current) return;

    hadCampaignFieldChangesBeforeSave.current = false;

    await new Promise((resolve) => setTimeout(resolve, 150));

    await refreshAfterSave({ saveFlow: 'campaignAllocationFieldsChanged' });
  }, [dispatch, refreshAfterSave]);

  return { captureChangesBeforeSave, handleAfterSave };
};
